"use client"

import type React from "react"

import { useState, useEffect } from "react"
import { motion } from "framer-motion"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Phone, MapPin, Clock, Send, MessageCircle, PhoneCall, Mail, ExternalLink } from "lucide-react"
import TextReveal from "@/components/animated/text-reveal"
import FloatingCard from "@/components/animated/floating-card"
import GradientBackground from "@/components/animated/gradient-background"
import { businessInfo } from "@/lib/metadata"
import axios from "axios"
import { toast } from "@/components/ui/use-toast"
interface ContactPageClientProps {
  productName?: string | null
}

export default function ContactPageClient({ productName }: ContactPageClientProps) {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    message: productName
      ? `I am interested in the following product: ${productName}\n\nPlease provide more details, pricing, and availability.`
      : "",
  })
  const [isMobile, setIsMobile] = useState(false);

  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768)
    }
    checkMobile()
    window.addEventListener("resize", checkMobile)
    return () => window.removeEventListener("resize", checkMobile)
  }, [])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)

    try {
      await axios.post("https://devcodzbackend.vercel.app/api/mail", {
        to: "realmetrometal@gmail.com",
        from: "realmetrometal@gmail.com",
        replyTo: formData.email,
        subject: `New Inquiry from ${formData.name}`,
        text: `
          Name: ${formData.name}
          Email: ${formData.email}
          Phone: ${formData.phone}
          Company: ${formData.company}
          Message: ${formData.message}
        `,
        html: `
          <p><strong>Name:</strong> ${formData.name}</p>
          <p><strong>Email:</strong> ${formData.email}</p>
          <p><strong>Phone:</strong> ${formData.phone}</p>
          <p><strong>Company:</strong> ${formData.company}</p>
          <p><strong>Message:</strong><br />${formData.message}</p>
        `,
      })

      toast({
        title: "Success",
        description: "Your message has been sent successfully!",
        variant: "default",
      })
      setFormData({ name: "", email: "", phone: "", company: "", message: "" })
    } catch (error) {
      console.error("Error sending message:", error)
      toast({
        title: "Error",
        description: "Failed to send your message. Please try again later.",
        variant: "destructive",
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }))
  }

  const handleWhatsAppClick = () => {
    const message = encodeURIComponent("Hello! I'm interested in your hardware products. Can you help me?")
    const whatsappNumber = businessInfo.telephone.replace(/[^0-9]/g, "")
    window.open(`https://wa.me/${whatsappNumber}?text=${message}`, "_blank")

  }

  return (
    <div className="min-h-screen relative">
      <GradientBackground />

      {/* Hero Section */}
      <section className="bg-slate-900 text-white py-16 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-600/20 via-purple-600/20 to-cyan-600/20" />
        <div className="container mx-auto px-4 relative z-10">
          <div className="text-center">
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6 }}
            >
              <Badge className="bg-white/10 text-white border-white/20 mb-6 backdrop-blur-sm">Contact Us</Badge>
            </motion.div>
            <TextReveal>
              <h1 className="text-3xl lg:text-5xl font-bold mb-6">Contact Real Metro Metal for Industrial Hardware Quotes</h1>
            </TextReveal>
            <TextReveal delay={0.2}>
              <p className="text-xl text-slate-300 max-w-3xl mx-auto leading-relaxed">
                Ready to discuss your hardware needs? Contact us today for competitive quotes and expert advice.
              </p>
            </TextReveal>
          </div>
        </div>
      </section>

      <div className="container mx-auto px-4 py-12 relative z-10">
        {/* Quick Contact Actions */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mb-12"
        >
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* WhatsApp Contact */}
            <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
              <Card
                className="bg-gradient-to-br from-green-500 to-green-600 text-white border-0 shadow-lg hover:shadow-2xl transition-all duration-300 cursor-pointer"
                onClick={handleWhatsAppClick}
              >
                <CardContent className="p-6 text-center">
                  <MessageCircle className="h-12 w-12 mx-auto mb-4" />
                  <h3 className="font-bold text-lg mb-2">WhatsApp Chat</h3>
                  <p className="text-green-100 text-sm mb-4">Get instant responses on WhatsApp</p>
                  <div className="flex items-center justify-center text-sm">
                    <span>Chat Now</span>
                    <ExternalLink className="h-4 w-4 ml-2" />
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            {/* Phone Contact */}
            <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
              <Card
                className="bg-gradient-to-br from-blue-500 to-blue-600 text-white border-0 shadow-lg hover:shadow-2xl transition-all duration-300 cursor-pointer"
                onClick={() => window.open(`tel:${businessInfo.telephone}`, "_self")}
              >
                <CardContent className="p-6 text-center">
                  <PhoneCall className="h-12 w-12 mx-auto mb-4" />
                  <h3 className="font-bold text-lg mb-2">Call Direct</h3>
                  <p className="text-blue-100 text-sm mb-4">Speak with our hardware experts</p>
                  <div className="flex items-center justify-center text-sm">
                    <span>{businessInfo.telephone}</span>
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            {/* Email Contact */}
            <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
              <Card
                className="bg-gradient-to-br from-purple-500 to-purple-600 text-white border-0 shadow-lg hover:shadow-2xl transition-all duration-300 cursor-pointer"
                onClick={() => window.open(`mailto:${businessInfo.email}`, "_self")}
              >
                <CardContent className="p-6 text-center">
                  <Mail className="h-12 w-12 mx-auto mb-4" />
                  <h3 className="font-bold text-lg mb-2">Send Email</h3>
                  <p className="text-purple-100 text-sm mb-4">Email us your requirements</p>
                  <div className="flex items-center justify-center text-sm">
                    <span>Send Message</span>
                    <ExternalLink className="h-4 w-4 ml-2" />
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-12">
          {/* Contact Information & Map */}
          <div className="space-y-8">
            {/* Contact Details */}
            <FloatingCard delay={0.1}>
              <Card className="bg-white/90 backdrop-blur-sm border-0 shadow-xl">
                <CardHeader>
                  <CardTitle className="flex items-center space-x-3">
                    <div className="bg-gradient-to-br from-slate-900 to-slate-700 text-white p-3 rounded-xl">
                      <MapPin className="h-6 w-6" />
                    </div>
                    <span className="text-xl">Visit Our Showroom</span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="space-y-4">
                    <div className="flex items-start space-x-4">
                      <div className="bg-slate-100 p-2 rounded-lg">
                        <MapPin className="h-5 w-5 text-slate-600" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-slate-900">Address</h4>
                        <p className="text-slate-600 leading-relaxed">
                          {businessInfo.address.streetAddress}
                          <br />
                          {businessInfo.address.addressLocality} {businessInfo.address.postalCode}
                          <br />
                          Sri Lanka
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start space-x-4">
                      <div className="bg-slate-100 p-2 rounded-lg">
                        <Clock className="h-5 w-5 text-slate-600" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-slate-900">Business Hours</h4>
                        <div className="text-slate-600 space-y-1">
                          <p>Monday - Saturday: 8:00 AM - 6:00 PM</p>
                          <p>Sunday: Closed</p>
                          <p className="text-sm text-green-600 font-medium">Currently Open</p>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-start space-x-4">
                      <div className="bg-slate-100 p-2 rounded-lg">
                        <Phone className="h-5 w-5 text-slate-600" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-slate-900">Contact Numbers</h4>
                        <div className="space-y-2">
                          <div className="flex items-center space-x-2">
                            <span className="text-slate-600">Mobile:</span>
                            <a
                              href={`tel:${businessInfo.telephone}`}
                              className="text-blue-600 hover:text-blue-800 font-medium"
                            >
                              {businessInfo.telephone}
                            </a>
                          </div>
                          <div className="flex items-center space-x-2">
                            <span className="text-slate-600">Hotline:</span>
                            <a
                              href={`tel:${businessInfo.whatsapp}`}
                              className="text-blue-600 hover:text-blue-800 font-medium"
                            >
                              {businessInfo.whatsapp}
                            </a>
                          </div>
                          <div className="flex items-center space-x-2">
                            <span className="text-slate-600">Landline:</span>
                            <a href="tel:+94112390293" className="text-blue-600 hover:text-blue-800 font-medium">
                              +94 11 239 0293
                            </a>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </FloatingCard>


          </div>

          {/* Contact Form */}
          <div>
            <FloatingCard delay={0.3}>
              <Card className="bg-white/90 backdrop-blur-sm border-0 shadow-xl">
                <CardHeader>
                  <CardTitle className="text-2xl flex items-center space-x-3">
                    <div className="bg-gradient-to-br from-slate-900 to-slate-700 text-white p-3 rounded-xl">
                      <Send className="h-6 w-6" />
                    </div>
                    <span>Send us a Message</span>
                  </CardTitle>
                  <p className="text-slate-600 leading-relaxed">
                    Fill out the form below and we'll get back to you within 24 hours with a detailed quote.
                  </p>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="name" className="block text-sm font-semibold text-slate-700 mb-2">
                          Full Name *
                        </label>
                        <Input
                          id="name"
                          name="name"
                          type="text"
                          required
                          value={formData.name}
                          onChange={handleChange}
                          placeholder="Your full name"
                          className="border-2 border-slate-200 focus:border-blue-500 bg-white/80 transition-all duration-300"
                        />
                      </div>
                      <div>
                        <label htmlFor="email" className="block text-sm font-semibold text-slate-700 mb-2">
                          Email Address *
                        </label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          required
                          value={formData.email}
                          onChange={handleChange}
                          placeholder="your@email.com"
                          className="border-2 border-slate-200 focus:border-blue-500 bg-white/80 transition-all duration-300"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="phone" className="block text-sm font-semibold text-slate-700 mb-2">
                          Phone Number *
                        </label>
                        <Input
                          id="phone"
                          name="phone"
                          type="tel"
                          required
                          value={formData.phone}
                          onChange={handleChange}
                          placeholder="+94 XX XXX XXXX"
                          className="border-2 border-slate-200 focus:border-blue-500 bg-white/80 transition-all duration-300"
                        />
                      </div>
                      <div>
                        <label htmlFor="company" className="block text-sm font-semibold text-slate-700 mb-2">
                          Company Name
                        </label>
                        <Input
                          id="company"
                          name="company"
                          type="text"
                          value={formData.company}
                          onChange={handleChange}
                          placeholder="Your company name"
                          className="border-2 border-slate-200 focus:border-blue-500 bg-white/80 transition-all duration-300"
                        />
                      </div>
                    </div>

                    <div>
                      <label htmlFor="message" className="block text-sm font-semibold text-slate-700 mb-2">
                        Message *
                      </label>
                      <Textarea
                        id="message"
                        name="message"
                        required
                        rows={6}
                        value={formData.message}
                        onChange={handleChange}
                        placeholder="Tell us about your hardware needs, quantities, project details, or any specific requirements..."
                        className="border-2 border-slate-200 focus:border-blue-500 bg-white/80 transition-all duration-300 resize-none"
                      />
                    </div>

                    <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                      <Button
                        type="submit"
                        size="lg"
                        disabled={isSubmitting}
                        className="w-full bg-gradient-to-r from-slate-900 to-slate-700 hover:from-slate-800 hover:to-slate-600 shadow-lg text-lg py-6"
                      >
                        {isSubmitting ? "Sending..." : <><Send className="h-5 w-5 mr-3" /> Send Message & Get Quote</>}
                      </Button>

                    </motion.div>

                    <div className="text-center">
                      <p className="text-sm text-slate-500">
                        Or contact us directly via{" "}
                        <button
                          type="button"
                          onClick={handleWhatsAppClick}
                          className="text-green-600 hover:text-green-700 font-medium underline"
                        >
                          WhatsApp
                        </button>{" "}
                        for faster response
                      </p>
                    </div>
                  </form>
                </CardContent>
              </Card>
            </FloatingCard>
          </div>
        </div>
        {/* Map */}
        <div className="gap-12 mb-12">
          <FloatingCard delay={0.2}>
            <Card className="bg-white/90 backdrop-blur-sm border-0 shadow-xl overflow-hidden">
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span>Find Us on Map</span>
                  <Button size="sm" variant="outline" asChild>
                    <a
                      href="https://www.google.com/maps?ll=6.940407,79.863568&z=15&t=m&hl=en&gl=LK&mapclient=embed&cid=17972891323868562216"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center space-x-2"
                    >
                      <span>Open in Maps</span>
                      <ExternalLink className="h-4 w-4" />
                    </a>
                  </Button>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="relative w-full h-80 bg-slate-100">
                  <iframe
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d75620.0499343587!2d79.82796966015846!3d6.9455546542740825!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3ae2590035391c5b%3A0xf96c896f41e2c328!2sReal%20metro%20metal!5e0!3m2!1sen!2slk!4v1716784963588!5m2!1sen!2slk"
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    allowFullScreen
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="Real Metro Metal Location - 392/1/3 Old Moor St, Colombo"
                    className="w-full h-full rounded-b-lg"
                  />
                </div>
              </CardContent>
            </Card>
          </FloatingCard>
        </div>

        {/* Additional Information */}
        <FloatingCard delay={0.4}>
          <Card className="bg-gradient-to-r from-slate-50 to-blue-50 border-0 shadow-lg">
            <CardContent className="p-8">
              <div className="text-center mb-6">
                <h3 className="text-2xl font-bold text-slate-900 mb-2">Why Choose Real Metro Metal?</h3>
                <p className="text-slate-600">Your trusted partner for quality industrial hardware solutions</p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="text-center">
                  <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Phone className="h-8 w-8 text-blue-600" />
                  </div>
                  <h4 className="font-semibold text-slate-900 mb-2">24/7 Support</h4>
                  <p className="text-sm text-slate-600">Always available to help with your hardware needs</p>
                </div>
                <div className="text-center">
                  <div className="bg-green-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                    <MessageCircle className="h-8 w-8 text-green-600" />
                  </div>
                  <h4 className="font-semibold text-slate-900 mb-2">Quick Response</h4>
                  <p className="text-sm text-slate-600">Fast quotes and immediate assistance via WhatsApp</p>
                </div>
                <div className="text-center">
                  <div className="bg-purple-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                    <MapPin className="h-8 w-8 text-purple-600" />
                  </div>
                  <h4 className="font-semibold text-slate-900 mb-2">Prime Location</h4>
                  <p className="text-sm text-slate-600">Conveniently located in the heart of Colombo</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </FloatingCard>

        {/* Mobile bottom spacing */}
        {isMobile && <div className="h-20" />}
      </div>
    </div>
  )
}
